import { Button, Eyebrow, Icon, IconTile, ImagePlate, Stat } from "@/ds";
import { hero, profile, roles } from "@/content/portfolio";
import { useRoleRotation } from "@/lib/useRoleRotation";

export function Hero() {
  const { text, show } = useRoleRotation(roles);

  /* The two blocks that fill the bento notches at desktop and fall back into
     normal document flow once the cut is dropped. */
  const eyebrowRow = (
    <div className="flex items-center justify-between gap-4 w-full">
      <Eyebrow>{hero.eyebrow}</Eyebrow>
      <div className="flex gap-3">
        <IconTile href={profile.linkedinUrl} label="LinkedIn">
          <Icon name="linkedin" size={16} />
        </IconTile>
        <IconTile href={`mailto:${profile.email}`} label="Email">
          <Icon name="mail" size={16} />
        </IconTile>
      </div>
    </div>
  );

  const statBlock = (
    <Stat
      size="lg"
      labelTone="label"
      value={hero.stat.value}
      label={
        <>
          {hero.stat.label[0]}
          <br />
          {hero.stat.label[1]}
        </>
      }
    />
  );

  return (
    <section id="top" className="px-gutter pt-hero-y pb-7">
      <div className="mx-auto w-full max-w-band">
        {/* ≥900px — the bento plate. Its notches are functional, so the eyebrow
            row and the statistic are layered into them. Once per page, hero only. */}
        <div className="hidden md:block">
          <ImagePlate
            cut="bento"
            src={hero.image}
            alt={hero.imageAlt}
            overlay={
              <>
                <div className="absolute top-0 left-0 w-[84%] h-[13%] flex items-center pr-5">{eyebrowRow}</div>
                <div className="absolute right-0 bottom-0 w-[21%] h-[26%] flex flex-col items-end justify-center text-right">
                  {statBlock}
                </div>
              </>
            }
          />
        </div>

        {/* <900px — the cut is dropped entirely rather than squashed, so its
            notch content returns to the flow above and below a square plate. */}
        <div className="md:hidden flex flex-col gap-5">
          {eyebrowRow}
          <ImagePlate src={hero.image} alt={hero.imageAlt} ratio="4/3" />
          {statBlock}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1.75fr)_minmax(0,0.8fr)] gap-7 md:gap-[clamp(48px,7vw,112px)] items-start pt-5">
          <div>
            <h1 className="font-display font-regular text-display-1 max-w-[20ch] mb-6">{hero.headline}</h1>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(min(260px,100%),1fr))] gap-6 max-w-[64ch] text-body text-text-body">
              {hero.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </div>

          {/* Right-alignment only reads correctly as the narrow column of the
              asymmetric pair — it flips to left once the grid stacks. */}
          <div className="flex flex-col items-start md:items-end text-left md:text-right gap-6 border-t border-border-hairline pt-[20px]">
            <div className="flex flex-col items-start md:items-end gap-[14px]">
              <span className="font-label text-label uppercase text-text-muted">{hero.currentlyLabel}</span>
              <span className="block text-[15px] leading-[1.7] text-text-primary max-w-[26ch] min-h-[3.4em]">
                <span
                  className="inline-block transition-[opacity,transform] duration-[240ms] ease-out"
                  style={{ opacity: show ? 1 : 0, transform: show ? "translateY(0)" : "translateY(-6px)" }}
                >
                  {text}
                </span>
              </span>
            </div>

            <p className="font-display text-[24px] leading-[1.3] max-w-[22ch]">{hero.availability}</p>

            <Button variant="accent" size="lg" href="#contact">
              {hero.ctaLabel}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
